import React, { ReactNode, useEffect, useMemo, useState } from "react";
import { Button, Checkbox, Dropdown, Input, Tag } from "antd";
import { DownOutlined, SearchOutlined } from "@ant-design/icons";
import { LocationDto } from "../../generated/dashboard-client";
import { DesignView } from "./EditScreenContentModal";
import { useMultiLocationDesigns } from "./useMultiLocationDesigns";
import LocationIconRound from "../icons/LocationIconRound";
import { Spinner } from "../account/misc/Spinner";
import SelectableCard from "../common/SelectableCard";
import RendererThumb from "../common/RendererThumb";
import { getSizeLabel, getSizeDimensions } from "../../util/utils";
import { env } from "../../env/env";
import "../design/DesignCard.scss";
import "./SavedDesignsPicker.scss";

export function designKey(design: Pick<DesignView, "id" | "page">) {
  return `${design.id}?${design.page}`;
}

interface SavedDesignsPickerProps {
  locations: LocationDto[];
  defaultLocationId: number;
  currentLocationId?: number;
  currentLocationBadgeLabel?: string;
  selectedId?: string | null;
  onSelect: (design: DesignView) => void;
  headerExtra?: ReactNode;
  transformDesigns?: (designs: DesignView[]) => DesignView[];
  emptyState?: ReactNode;
}

export default function SavedDesignsPicker({
  locations,
  defaultLocationId,
  currentLocationId,
  currentLocationBadgeLabel,
  selectedId,
  onSelect,
  headerExtra,
  transformDesigns,
  emptyState,
}: SavedDesignsPickerProps) {
  const allLocationIds = useMemo(
    () => locations.map(l => l.internalId!).filter(id => id != null),
    [locations]
  );
  const badgeLocationId = currentLocationId ?? defaultLocationId;

  const [appliedLocationIds, setAppliedLocationIds] = useState([defaultLocationId]);
  const [pendingLocationIds, setPendingLocationIds] = useState([defaultLocationId]);
  const [filterOpen, setFilterOpen] = useState(false);
  const [locationSearch, setLocationSearch] = useState("");
  const [search, setSearch] = useState("");

  useEffect(() => {
    setAppliedLocationIds([defaultLocationId]);
    setPendingLocationIds([defaultLocationId]);
  }, [defaultLocationId]);

  const { designs, loading } = useMultiLocationDesigns(appliedLocationIds);

  const shapedDesigns = transformDesigns ? transformDesigns(designs) : designs;

  const allSelected = allLocationIds.length > 0 && pendingLocationIds.length === allLocationIds.length;

  function togglePending(id: number, checked: boolean) {
    setPendingLocationIds(prev => (checked ? [...prev, id] : prev.filter(x => x !== id)));
  }

  function toggleAll(checked: boolean) {
    setPendingLocationIds(checked ? [...allLocationIds] : []);
  }

  function applyFilter() {
    setAppliedLocationIds(pendingLocationIds.length ? pendingLocationIds : [defaultLocationId]);
    setFilterOpen(false);
  }

  function clearFilter() {
    setPendingLocationIds([]);
  }

  function openChange(open: boolean) {
    setFilterOpen(open);
    if (open) setPendingLocationIds(appliedLocationIds);
  }

  const locationName = (id: number) => locations.find(l => l.internalId === id)?.name ?? "";

  const filterLabel = (() => {
    if (allLocationIds.length > 0 && appliedLocationIds.length === allLocationIds.length) return "All locations";
    if (appliedLocationIds.length === 1) return locationName(appliedLocationIds[0]);
    return `${appliedLocationIds.length} locations`;
  })();

  const filteredLocations = locations.filter(
    l => !locationSearch || (l.name ?? "").toLowerCase().includes(locationSearch.toLowerCase())
  );

  const groups = useMemo(() => {
    const term = search.trim().toLowerCase();
    const visible = shapedDesigns.filter(d => !term || d.name.toLowerCase().includes(term));
    const orderedIds = [
      ...appliedLocationIds.filter(id => id === badgeLocationId),
      ...appliedLocationIds.filter(id => id !== badgeLocationId),
    ];
    return orderedIds
      .map(id => ({ id, name: locationName(id), designs: visible.filter(d => d.locationId === id) }))
      .filter(g => g.designs.length > 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shapedDesigns, search, appliedLocationIds, badgeLocationId, locations]);

  const hasAny = groups.length > 0;

  const filterOverlay = (
    <div className="sdp-filter">
      <Input
        type="search"
        prefix={<SearchOutlined />}
        placeholder="Search locations"
        value={locationSearch}
        onChange={e => setLocationSearch(e.target.value)}
      />
      <div className="sdp-filter-list">
        <label className="sdp-filter-row">
          <Checkbox checked={allSelected} onChange={e => toggleAll(e.target.checked)} />
          <span>All locations</span>
        </label>
        {filteredLocations.map(loc => (
          <label className="sdp-filter-row" key={loc.internalId}>
            <Checkbox
              checked={pendingLocationIds.includes(loc.internalId!)}
              onChange={e => togglePending(loc.internalId!, e.target.checked)}
            />
            <span>{loc.name}</span>
          </label>
        ))}
      </div>
      <div className="sdp-filter-actions">
        <Button onClick={clearFilter}>Clear</Button>
        <Button type="primary" onClick={applyFilter}>Apply</Button>
      </div>
    </div>
  );

  return (
    <div className="saved-designs-picker">
      <div className="sdp-controls">
        <Input
          type="search"
          prefix={<SearchOutlined />}
          placeholder="Search by design name"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        {headerExtra}
        <Dropdown
          trigger={["click"]}
          placement="bottomRight"
          visible={filterOpen}
          onVisibleChange={openChange}
          getPopupContainer={trigger => trigger.parentNode as HTMLElement}
          overlay={filterOverlay}
        >
          <button type="button" className="sdp-filter-trigger">
            <LocationIconRound />
            <span>{filterLabel}</span>
            <DownOutlined />
          </button>
        </Dropdown>
      </div>

      {loading && (
        <div className="sdp-loading">
          <Spinner />
        </div>
      )}

      {!loading && !hasAny && (
        <div className="sdp-empty">{emptyState ?? <p>No designs found.</p>}</div>
      )}

      {!loading && hasAny && (
        <div className="sdp-groups">
          {groups.map(group => (
            <div className="sdp-group" key={group.id}>
              <div className="sdp-group-header">
                <LocationIconRound />
                <span className="sdp-group-name">{group.name}</span>
                {group.id === badgeLocationId && currentLocationBadgeLabel && (
                  <Tag className="sdp-current-badge">{currentLocationBadgeLabel}</Tag>
                )}
              </div>
              <ul className="sdp-designs">
                {group.designs.map(design => {
                  const key = designKey(design);
                  const isSelected = selectedId === key;
                  const sizeLabel = getSizeLabel(design.size ?? "");
                  const sizeDims = getSizeDimensions(design.size ?? "");
                  const unitSuffix = design.unit ? ` ${design.unit}` : "";
                  const hasCustomSize = !sizeLabel && !!design.width && !!design.height;
                  return (
                    <SelectableCard
                      key={key}
                      className="design-card"
                      selected={isSelected}
                      onMediaClick={() => onSelect(design)}
                      mediaSlot={
                        <RendererThumb
                          src={design.thumbnail}
                          alt={design.name}
                          loading="lazy"
                          fallbackSrc={`${env.dashboardPath}/thumb-placeholder.png`}
                        />
                      }
                      footerSlot={
                        <>
                          <div className="edit-name disable-rename">
                            <h6>{design.name}</h6>
                          </div>
                          {sizeLabel ? (
                            <span className="design-card-size">
                              <span className="size-label">{sizeLabel}</span> • {sizeDims}{unitSuffix}
                            </span>
                          ) : hasCustomSize ? (
                            <span className="design-card-size">
                              <span className="size-label">Custom</span> • {design.width} x {design.height}{unitSuffix}
                            </span>
                          ) : null}
                        </>
                      }
                    />
                  );
                })}
              </ul>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
